import ResponseCardPick from "@/components/game/ResponseCardPick";
import { bankCardSrc } from "@/lib/artAssets";
import { CountdownBar, CoverBody, DecidingBody, useAutoDecline, useResponseView, useServerCountdown } from "@/components/game/ResponseWindow";

// PRIVATE RESPONSE WINDOW (RULES 16 OCT) for game.response_state — same look, timer and privacy
// as the weather Protection window (ResponseWindow):
//   kind "board_thief": the victim of a confirmed Board Thief, holder or not —
//     can play: "Ana is playing Board Thief on your Urban Forest (row 2, col 3). Play Protection
//     Card to stop it?" + the card (PAY UPFRONT: free, paid when taken), Yes / No; anyone else: "... You have no response." Continue.
//     No answer = No.
//   kind "bank_vote" (v3 step 5, Climate Bank): every seat but the proposer answers Yes / No on its own
//     screen ("Ana proposes Living Coastline: ... Agree?"), 60 s, no answer = No; answers stay secret
//     until the vote ends (the log then names who agreed).
//   (TERRAIN 2 of 3: the Mangrove prompt is gone — a Mangrove the sea reaches is lost.)
//   every other screen: "Players are deciding on responses… 14s" (no names, no card, no buttons).
// responder: { online, seat } (ResponseWindow). Drawn inside the SELECTED CARD area (inline).
export default function ResponseFlow({ game, onDecide, busy, focus = null, responder = { online: false, seat: null } }) {
  const dim = { opacity: focus ? 0.5 : 1 };
  const st = game.response_state;
  const open = !!st;
  const win = open ? { responders: st.responders, answers: st.answers, options: st.options, current: st.current, deadline: st.deadline } : null;
  const view = useResponseView(win, responder);
  const { remaining, total, deadlineMs } = useServerCountdown(open ? st.deadline : null);
  const done = (open && view.color && st.partial?.[view.color]) || {};
  const item = open && view.color ? (st.items?.[view.color] || []).find((i) => !done[i.id]) : null;
  // Pass & Play: the browser gives the window's default answer when the asked player's timer runs out.
  const autoAnswer = () => item && onDecide(st.default === "yes", item.id);
  useAutoDecline({ enabled: open && !responder.online && view.mode !== "deciding" && !!item, remaining, deadlineMs, busy, onDecide: autoAnswer });

  if (!open) return null;
  const kind = st.kind;
  const player = game.players.find((p) => p.color === view.color);
  const answer = (yes) => onDecide(yes, item?.id);

  let title = "Responses";
  let body;
  if (view.mode === "deciding" || !item) {
    body = <DecidingBody remaining={remaining} />;
  } else if (view.mode === "cover") {
    body = <CoverBody name={player?.name || "Player"} onReveal={view.reveal} />;
  } else if (kind === "bank_vote" && item.option) {
    title = "Climate Bank vote";
    const art = bankCardSrc(item.project);
    body = (
      <div className="flex flex-col items-center" style={{ gap: 8 }}>
        {art && <img src={art} alt="" data-testid="bank-vote-art" style={{ width: 86, height: 138, borderRadius: 6, border: "1px solid var(--line)", objectFit: "cover" }} />}
        <p data-testid="response-text" style={{ fontSize: 13, lineHeight: "17px", color: "var(--ink)" }}>{item.text}</p>
        <div className="flex" style={{ gap: 8 }}>
          <button data-testid="bank-vote-yes" onClick={() => answer(true)} disabled={busy} className="btn-tactile btn-tactile--primary">Yes, agree</button>
          <button data-testid="bank-vote-no" onClick={() => answer(false)} disabled={busy} className="btn-tactile">No</button>
        </div>
      </div>
    );
  } else if (kind === "board_thief" && item.option) {
    title = "Board Thief on your square";
    body = (
      <div className="flex flex-col items-center" style={{ gap: 6 }}>
        <p data-testid="response-text" style={{ fontSize: 13, lineHeight: "17px", color: "var(--ink)" }}>{item.text}</p>
        <ResponseCardPick key={view.color} player={player} cardKey="protection_card" confirmLabel="Yes, block it" declineLabel="No" onDecide={answer} busy={busy} testPrefix="response" />
      </div>
    );
  } else {
    title = kind === "bank_vote" ? "Climate Bank vote" : "Board Thief on your square";
    body = (
      <div className="flex flex-col items-center" style={{ gap: 12 }}>
        <p data-testid="response-text" style={{ fontSize: 13, lineHeight: "17px", color: "var(--ink)" }}>{item.text}</p>
        <button data-testid="response-continue" onClick={() => answer(false)} disabled={busy} className="btn-tactile btn-tactile--primary">Continue</button>
      </div>
    );
  }

  return (
    <div data-testid="response-flow" data-kind={kind} data-mode={view.mode} className="w-full">
      <div className="w-full bg-[color:var(--card)] rounded-[10px] border-2 border-[color:var(--sea)] p-3 text-center flex flex-col" data-testid="response-box">
        <div className="focus-region shrink-0" data-focus-part="header" style={dim}>
          <div className="label-kicker text-[color:var(--ink-3)]">Private response</div>
          <h2 className="font-display text-lg leading-6 font-bold tracking-tight text-[color:var(--ink)] mt-1" data-testid="response-title">{title}</h2>
          <div data-testid="response-countdown" className="mt-2 label-kicker text-[color:var(--ink)] tabular-nums">{remaining}s to decide</div>
          <div className="mt-1 mx-auto" style={{ width: 200 }}>
            <CountdownBar remaining={remaining} total={total} testid="response-countdown-bar" />
          </div>
        </div>
        <div className="focus-region flex-1 min-h-0 flex flex-col mt-2" data-focus-part="response">
          {body}
        </div>
      </div>
    </div>
  );
}
